import { useState, useEffect, useRef } from "react";
import {
  saveToSS,
  getFromSS,
} from "@visiblethread/react-components/dist/utils";

const areColumnWidthsValid = (columnWidths, expectedLength) => {
  if (!Array.isArray(columnWidths)) {
    return false;
  }

  if (!columnWidths.length === expectedLength) {
    return false;
  }

  for (let i = 0; i < columnWidths.length; i++) {
    const columnWidth = columnWidths[i];
    if (typeof columnWidth !== "number") return false;
  }

  return true;
};

const getColumns = (headerRef) => {
  let columns;

  // Resizable container can be either from docs or from vt-components
  columns = Array.from(
    headerRef.current.getElementsByClassName("resizable_container")
  );

  if (columns.length === 0) {
    columns = Array.from(
      headerRef.current.getElementsByClassName("vt_resizable_container")
    );
  }

  return columns;
};

const resetFlexGrow = (columns) => {
  columns.forEach((column, index) => {
    // Last column is always flexible
    if (index === columns.length - 1) {
      column.style.flexGrow = 1;
      return;
    }

    column.style.flexGrow = 0;
  });
};

export const useColumnsResizing = (
  initColumnWidths,
  minimalColumnWidths,
  sessionStorageKey,
  headerRef
) => {
  const [columnWidths, setColumnWidths] = useState(initColumnWidths);
  const isFirstRender = useRef(true);

  const onColumnResize = (index, newWidth) => {
    setColumnWidths(
      columnWidths.map((columnWidth, columnIndex) => {
        if (index === columnIndex) {
          return newWidth;
        }
        return columnWidth;
      })
    );
  };

  // Get settings from session storage or measure current sizes
  useEffect(() => {
    if (!headerRef.current) return;

    const columns = getColumns(headerRef);

    const measureWidths = () => {
      const newColumnWidths = columns.map((column, index) => {
        if (column.clientWidth < minimalColumnWidths[index]) {
          return minimalColumnWidths[index];
        }

        return column.clientWidth;
      });

      setColumnWidths(newColumnWidths);
      resetFlexGrow(columns);
    };

    if (!sessionStorageKey) {
      measureWidths();
      return;
    }

    // If possible load columnWidths from SS, otherwise set current values
    try {
      const settingsStringified = getFromSS(sessionStorageKey);

      if (!settingsStringified) {
        throw new Error("SS Error: Settings are abscent.");
      }

      const settings = JSON.parse(settingsStringified);
      const columnWidthsFromSS = settings?.columnWidths;

      if (!areColumnWidthsValid(columnWidthsFromSS, columnWidths.length)) {
        throw new Error("SS Error: Invalid settings format.");
      }

      setColumnWidths(columnWidthsFromSS);
      resetFlexGrow(columns);
    } catch {
      measureWidths();
    }
  }, [headerRef?.current]);

  // Save settings to session storage
  useEffect(() => {
    if (!sessionStorageKey) return;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    saveToSS(
      sessionStorageKey,
      JSON.stringify({
        columnWidths,
      })
    );
  }, [columnWidths]);

  return { columnWidths, setColumnWidths, onColumnResize };
};
